import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Pagination, Response } from '@ui/src';
import { Lock, LockAuditLog } from '../../types';
import { individualCustomersApi } from '../services/individual';

import ROUTES from '../../../../routes';

export interface CustomerSecurityLocksState {
  locks?: Lock[];
  pagination?: Pagination;
  lockItem?: Lock;
  lockItemAuditLogs?: LockAuditLog[];
  lockItemAuditLogsPagination?: Pagination;
  locksTakeCount?: number;
  logsTakeCount?: number;
  logs?: LockAuditLog[];
  logsPagination?: Pagination;
  logItem?: LockAuditLog;
  takeCount?: number;
  organizationId?: string;
}

const initialState: CustomerSecurityLocksState = {};

export const customerSecurityLocksSlice = createSlice({
  name: 'customerSecurityLocks',
  initialState,
  reducers: {
    setOrganizationId: (state, action: PayloadAction<string>) => {
      state.organizationId = action.payload;
    },
    setLocksTakeCount: (state, action: PayloadAction<number>) => {
      state.locksTakeCount = action.payload;
    },
    setLogsTakeCount: (state, action: PayloadAction<number>) => {
      state.logsTakeCount = action.payload;
    },
    clearLogsState: (state) => {
      state.logs = undefined;
      state.logsPagination = undefined;
      state.logsTakeCount = undefined;
      state.organizationId = undefined;
    },
    clearLocksState: (state) => {
      state.locks = undefined;
      state.pagination = undefined;
      state.locksTakeCount = undefined;
      state.organizationId = undefined;
    },
    clearLocks: (state) => {
      state.locks = undefined;
      state.pagination = undefined;
    },
    clearLockPanelState: (state) => {
      state.lockItem = undefined;
      state.lockItemAuditLogs = undefined;
      state.lockItemAuditLogsPagination = undefined;
    },
    clearLogPanelState: (state) => {
      state.logItem = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      individualCustomersApi.endpoints.getCustomerLocks.matchFulfilled,
      (state, action: PayloadAction<Response<Lock>>) => {
        state.locks = action.payload.data.edges.map((item) => ({
          ...item.node,
          url: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOCKS_ITEM, {
            lockId: item.node.id,
            customerId: state.organizationId as string,
          }),
          type: item.node.createdByEmployeeId ? 'by Employee' : 'Auto',
        }));

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.getCustomerLockItem.matchFulfilled,
      (state, action: PayloadAction<{ data: Lock }>) => {
        state.lockItem = action.payload.data;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.updateCustomerLock.matchFulfilled,
      (state, action: PayloadAction<{ data: Lock }>) => {
        state.locks = state.locks?.map((item) => {
          let result = { ...item };

          if (result.id === action.payload.data.id) {
            result = {
              ...result,
              ...action.payload.data,
            };
          }

          return result;
        });

        state.lockItem = action.payload.data;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.getCustomerLockItemAuditLogs
        .matchFulfilled,
      (state, action: PayloadAction<Response<LockAuditLog>>) => {
        if (state.lockItem) {
          state.lockItemAuditLogs = action.payload.data.edges.map(
            (item) => item.node,
          );

          state.lockItemAuditLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.getCustomerLocksAuditLogs.matchFulfilled,
      (state, action: PayloadAction<Response<LockAuditLog>>) => {
        state.logs = action.payload.data.edges.map((item) => ({
          ...item.node,
          url: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOGS_ITEM, {
            logId: item.node.id,
            customerId: state.organizationId as string,
          }),
        }));

        state.logsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      individualCustomersApi.endpoints.getCustomerLocksAuditLogItem
        .matchFulfilled,
      (state, action: PayloadAction<{ data: LockAuditLog }>) => {
        state.logItem = action.payload.data;
      },
    );
  },
});

export const {
  clearLocksState,
  clearLockPanelState,
  setLogsTakeCount,
  setLocksTakeCount,
  setOrganizationId,
  clearLogPanelState,
  clearLogsState,
  clearLocks,
} = customerSecurityLocksSlice.actions;

export default customerSecurityLocksSlice.reducer;
